/*
 * Copyright (c) 2026 CalAmp Corp.  All Rights Reserved
 */
import {useCallback, useEffect, useMemo, useState} from 'react';
import isFunction from 'lodash.isfunction';
import {FilterDrawerCategoryBase} from './types';
import {
  mergeFilterDrawerCategories,
  orderFilterDrawerCategories
} from './utils';

interface UseFilterDrawerParams {
  categoryOrder?: string[];
  categories: FilterDrawerCategoryBase[];
  expandedIds?: string[];
  favorite: boolean;
  onCategoryOrderChange?: (categoryOrder: string[]) => void;
  onExpandedIdsChange?: (expandedIds: string[]) => void;
  onFavoriteChange?: (favorite: boolean) => void;
}

/**
 * Tracks filter drawer category order, expanded sections, and favorite state.
 *
 * ```typescript
 * import useFilterDrawer from '@ion/dls/src/components/drawer/useFilterDrawer';
 *
 * ...
 *
 * const filterDrawer = useFilterDrawer({
 *   categories,
 *   favorite,
 *   onFavoriteChange
 * });
 * ```
 */
export const useFilterDrawer = ({
  categoryOrder,
  categories,
  expandedIds,
  favorite,
  onCategoryOrderChange,
  onExpandedIdsChange,
  onFavoriteChange
}: UseFilterDrawerParams) => {
  const [orderedCategories, setOrderedCategories] = useState(() => (
    orderFilterDrawerCategories(categories, categoryOrder)
  ));
  const [localExpandedIds, setLocalExpandedIds]   = useState<string[]>([]);
  const [isFavorite, setIsFavorite]               = useState(favorite);
  const currentExpandedIds                        = expandedIds ?? localExpandedIds;

  const categoryIds = useMemo(() => (
    orderedCategories.map(({id}) => id)
  ), [orderedCategories]);

  const allExpanded = (
    categoryIds.length > 0 && categoryIds.every(id => currentExpandedIds.includes(id))
  );

  const expandedLookup = useMemo(() => (
    currentExpandedIds.reduce(
      (lookup, id) => ({...lookup, [id]: true}),
      {} as Record<string, boolean>
    )
  ), [currentExpandedIds]);

  const draggableCategories = useMemo(() => (
    orderedCategories.map(category => ({
      ...category,
      primaryText : category.label
    }))
  ), [orderedCategories]);

  useEffect(() => {
    setOrderedCategories(currentCategories => orderFilterDrawerCategories(
      mergeFilterDrawerCategories(currentCategories, categories),
      categoryOrder
    ));
  }, [categories, categoryOrder]);

  useEffect(() => {
    setIsFavorite(favorite);
  }, [favorite]);

  useEffect(() => {
    const removeUnknownIds = (ids: string[]) => {
      const currentIds = ids.filter(id => categoryIds.includes(id));
      return currentIds.length === ids.length ? ids : currentIds;
    };
    if (expandedIds) {
      const nextIds = removeUnknownIds(expandedIds);
      if (nextIds !== expandedIds) {
        onExpandedIdsChange?.(nextIds);
      }
    } else {
      setLocalExpandedIds(removeUnknownIds);
    }
  }, [categoryIds, expandedIds, onExpandedIdsChange]);

  const setExpandedIds = useCallback((
    update: string[] | ((currentIds: string[]) => string[])
  ) => {
    if (expandedIds) {
      const nextIds = isFunction(update) ? update(expandedIds) : update;
      if (nextIds !== expandedIds) {
        onExpandedIdsChange?.(nextIds);
      }
    } else {
      setLocalExpandedIds(update);
    }
  }, [expandedIds, onExpandedIdsChange]);

  const toggleExpanded = useCallback((id: string) => {
    setExpandedIds(ids => ids.includes(id)
      ? ids.filter(expandedId => expandedId !== id)
      : [...ids, id]
    );
  }, [setExpandedIds]);

  const toggleAll = useCallback(() => {
    setExpandedIds(allExpanded ? [] : categoryIds);
  }, [allExpanded, categoryIds, setExpandedIds]);

  const toggleFavorite = useCallback(() => {
    setIsFavorite(value => {
      onFavoriteChange?.(!value);
      return !value;
    });
  }, [onFavoriteChange]);

  const handleCategoryDragEnd = useCallback((items: FilterDrawerCategoryBase[]) => {
    setOrderedCategories(items);
    onCategoryOrderChange?.(items.map(({id}) => id));
  }, [onCategoryOrderChange]);

  return {
    allExpanded,
    draggableCategories,
    expandedLookup,
    handleCategoryDragEnd,
    isFavorite,
    toggleAll,
    toggleExpanded,
    toggleFavorite
  };
};

export default useFilterDrawer;
